import React, { useState, useEffect } from 'react'
import { styled } from 'styled-components'
import { sortDataSet } from './functions'
import { FaChevronLeft, FaChevronRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa'

interface Column {
  header: string
  accessor: string
}

interface TableRowData {
  [key: string]: any
}

interface TableProps {
  columns: Column[]
  data: TableRowData[]
  cellRenderer: (dataKey: string, row: any) => JSX.Element
  pageSize?: number
}

const TableWrapper = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  background-color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  margin: 20px 0;
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`

const TableHeader = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 500;
  background-color: #1976d2;
  color: white;
  position: relative;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  transition: background-color 0.2s ease;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background-color: #1565c0;
    cursor: pointer;
  }
`

const SortIconContainer = styled.div`
  display: inline-flex;
  margin-left: 4px;
  vertical-align: middle;
`

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f5f5f5;
  }

  &:hover {
    background-color: rgba(25, 118, 210, 0.04);
    transition: background-color 0.2s ease;
  }
`

const TableCell = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: middle;
`

const SelectionCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  border-radius: 3px;
  border: 2px solid #1976d2;

  &:checked {
    accent-color: #1976d2;
  }
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
`

const PageInfo = styled.div`
  margin-right: 24px;
  font-weight: 500;
`

const PaginationButton = styled.button`
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(25, 118, 210, 0.08);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;

    &:hover {
      background-color: transparent;
    }
  }
`

const EnhancedTable: React.FC<TableProps> = ({ columns, data, cellRenderer, pageSize = 10 }) => {
  const [sortedData, setSortedData] = useState<TableRowData[]>(data)
  const [sortingKey, setSortingKey] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [selectAll, setSelectAll] = useState<boolean>(false)

  useEffect(() => {
    setSortedData(data)
    // Reset to page 1 if data changes
    setCurrentPage(1)
    // Reset selection when data changes
    setSelectedRows(new Set())
    setSelectAll(false)
  }, [data])

  // Start and end index for current page
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = Math.min(startIndex + pageSize, sortedData.length)

  // Current page data
  const paginatedData = sortedData.slice(startIndex, endIndex)
  const totalPages = Math.ceil(sortedData.length / pageSize)

  const handleSort = (key: string) => {
    let newDirection

    if (sortingKey === key) {
      // Cycle through: no sort -> ascending -> descending -> no sort
      newDirection =
        sortDirection === 0 ? 1
        : sortDirection === 1 ? -1
        : 0
    } else {
      // Default to ascending for a new column
      newDirection = 1
    }

    setSortingKey(key)
    setSortDirection(newDirection)

    if (newDirection === 0) {
      // Reset to original data order
      setSortedData([...data])
    } else {
      setSortedData(sortDataSet([...data], key, newDirection))
    }
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set())
    } else {
      const allRowIndexes = sortedData.map((_, index) => index)
      setSelectedRows(new Set(allRowIndexes))
    }
    setSelectAll(!selectAll)
  }

  const handleSelectRow = (index: number) => {
    const newSelected = new Set(selectedRows)
    if (newSelected.has(index)) {
      newSelected.delete(index)
    } else {
      newSelected.add(index)
    }
    setSelectedRows(newSelected)

    // Update selectAll state
    setSelectAll(newSelected.size === sortedData.length)
  }

  const getSortIcon = (key: string) => {
    if (sortingKey !== key) return <FaSort size={14} />
    if (sortDirection === 1) return <FaSortUp size={14} />
    if (sortDirection === -1) return <FaSortDown size={14} />
    return <FaSort size={14} />
  }

  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          <tr>
            <TableHeader style={{ width: '48px', padding: '8px 16px' }}>
              <SelectionCheckbox
                checked={selectAll}
                onChange={handleSelectAll}
              />
            </TableHeader>
            {columns.map((column, index) => (
              <TableHeader
                key={index}
                onClick={() => handleSort(column.accessor)}
              >
                {column.header}
                <SortIconContainer>{getSortIcon(column.accessor)}</SortIconContainer>
              </TableHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((row, rowIndex) => {
            const dataIndex = startIndex + rowIndex
            return (
              <TableRow key={dataIndex}>
                <TableCell style={{ width: '48px', padding: '8px 16px' }}>
                  <SelectionCheckbox
                    checked={selectedRows.has(dataIndex)}
                    onChange={() => handleSelectRow(dataIndex)}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell key={column.accessor}>{cellRenderer(column.accessor, row)}</TableCell>
                ))}
              </TableRow>
            )
          })}
        </tbody>
      </StyledTable>

      {totalPages > 1 && (
        <PaginationContainer>
          <PageInfo>
            {startIndex + 1}–{endIndex} z {sortedData.length}
          </PageInfo>
          <PaginationButton
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <FaChevronLeft />
          </PaginationButton>
          <PaginationButton
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <FaChevronRight />
          </PaginationButton>
        </PaginationContainer>
      )}
    </TableWrapper>
  )
}

export default EnhancedTable
